import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from "react";
import { Link } from "react-router";

export type DuviNicheSiteButtonVariant = "primary" | "secondary" | "text";
export type DuviNicheSiteButtonSize = "default" | "lg";

export interface DuviNicheSiteButtonProps {
  children: ReactNode;
  variant?: DuviNicheSiteButtonVariant;
  size?: DuviNicheSiteButtonSize;
  /** Renders a plain `<a>` — for the cross-origin hand-off to the business
   * portal at `app.<niche-domain>`. Mutually exclusive with `to`. */
  href?: string;
  /** Renders a router `<Link>` — for same-site navigation between this
   * brochure's two routes. Mutually exclusive with `href`. */
  to?: string;
  type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
  "aria-label"?: string;
}

const SIZES: Record<DuviNicheSiteButtonSize, string> = {
  default: "text-[15px] px-[18px] py-[10px]",
  lg: "text-[17px] px-[26px] py-[14px]",
};

const VARIANTS: Record<DuviNicheSiteButtonVariant, string> = {
  primary:
    "bg-[var(--brand)] text-[color:var(--brand-foreground)] border border-transparent hover:bg-[var(--brand-hover)] active:bg-[var(--brand-hover)] active:translate-y-px shadow-[var(--shadow-control)]",
  secondary:
    "bg-transparent text-[color:var(--brand)] border border-[var(--brand)] hover:bg-[var(--tint)] active:bg-[var(--tint)] active:translate-y-px",
  text: "bg-transparent text-[color:var(--brand)] border border-transparent px-0 py-0 hover:underline hover:text-[color:var(--brand-hover)]",
};

/**
 * DuviNicheSiteButton — the brochure's single button primitive, ported from
 * the bespoke Fishwalkers design system's `components/core/Button.jsx`
 * (duvi-design/exports/niches/fishwalkers/system.zip).
 *
 * Primary darkens to `--brand-hover`; secondary fills with `--tint`; text
 * underlines. Press states translate down 1px rather than scaling, and only
 * the disabled state uses opacity — the system's committed hover/press
 * rules. One `primary` button per section.
 *
 * The export knows only `href`; a router `to` is added here because the real
 * site is two routes rather than the prototype's two hash-anchored artboards
 * on one canvas. The class list is otherwise the export's.
 *
 * A link-button's ink and underline are the utilities' to set, not `a`'s —
 * `src/styles/tailwind.css` keeps the element defaults inside `@layer base`
 * so these classes win. Move them out of that layer and every primary button
 * paints brand ink on a brand fill.
 */
export function DuviNicheSiteButton({
  children,
  variant = "primary",
  size = "default",
  href,
  to,
  type = "button",
  disabled = false,
  fullWidth = false,
  className = "",
  onClick,
  "aria-label": ariaLabel,
}: DuviNicheSiteButtonProps) {
  const classes = [
    "inline-flex items-center justify-center gap-2 font-[family-name:var(--font-body)] font-semibold no-underline rounded-[var(--radius-control)]",
    "transition-[background-color,color,transform,box-shadow] duration-[var(--duration-base)] ease-[var(--ease-standard)]",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]",
    VARIANTS[variant],
    variant === "text" ? "" : SIZES[size],
    fullWidth ? "w-full" : "",
    disabled ? "opacity-45 pointer-events-none" : "cursor-pointer",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (to && !disabled) {
    return (
      <Link to={to} className={classes} aria-label={ariaLabel}>
        {children}
      </Link>
    );
  }

  if (href && !disabled) {
    const anchorProps: AnchorHTMLAttributes<HTMLAnchorElement> = {
      href,
      className: classes,
      "aria-label": ariaLabel,
    };
    return <a {...anchorProps}>{children}</a>;
  }

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
